import Link from 'next/link'
import { GetServerSideProps } from 'next'
import { fetchRules, RuleCard, CATEGORY_LABELS } from '@/lib/rulesApi'
import { GAME_LABELS } from '@/lib/games'
import { absUrl, breadcrumbLd, SeoProps } from '@/utils/seo'

type Props = { game: string; gameLabel: string; rules: RuleCard[]; q: string; category: string }

const SOURCES: Array<{ key: string; label: string }> = [
  { key: '', label: '全部' },
  { key: 'official_national', label: '国家官方' },
  { key: 'world', label: '世界' },
  { key: 'provincial', label: '地方' },
  { key: 'institutional', label: '配套制度' },
  { key: 'event', label: '赛事规程' },
]

export default function GamePage({ game, gameLabel, rules, q, category }: Props) {
  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <nav className="mb-3 text-sm text-gray-400">
            <Link href="/rules" className="text-gray-600 hover:text-gray-900">
              灵缇规则库
            </Link>{' '}
            / {gameLabel}
          </nav>
          <h1 className="mb-6 text-2xl font-black text-gray-900 md:text-3xl">{gameLabel}竞赛规则</h1>

          <form className="mb-5 flex max-w-xl gap-2" method="get">
            <input
              name="q"
              defaultValue={q}
              placeholder="搜索规则名称 / 发布机构 / 关键词"
              className="flex-1 rounded-lg border border-gray-200 bg-white px-4 py-2 shadow-sm outline-none focus:border-gray-900"
            />
            {category ? <input type="hidden" name="category" value={category} /> : null}
            <button
              type="submit"
              className="rounded-lg bg-brand px-5 py-2 font-medium text-white hover:bg-brand-dark"
            >
              搜索
            </button>
          </form>

          <div className="mb-8 flex flex-wrap gap-2">
            {SOURCES.map((s) => {
              const href = {
                pathname: `/rules/g/${game}`,
                query: { ...(q ? { q } : {}), ...(s.key ? { category: s.key } : {}) },
              }
              const active = s.key === category
              return (
                <Link
                  key={s.key || 'all'}
                  href={href}
                  className={`rounded-full px-4 py-1.5 text-sm ${
                    active ? 'bg-ink text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {s.label}
                </Link>
              )
            })}
          </div>

          {rules.length === 0 ? (
            <p className="py-20 text-center text-gray-400">该项目暂无规则，欢迎上传补充</p>
          ) : (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {rules.map((r) => (
                <Link
                  key={r.slug}
                  href={`/rules/${r.slug}`}
                  className="group rounded-2xl border border-gray-100 bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
                >
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                      {CATEGORY_LABELS[r.category]}
                    </span>
                    {r.is_authoritative ? (
                      <span className="rounded bg-red-50 px-2 py-0.5 text-xs text-red-600">官方</span>
                    ) : null}
                    {r.year ? <span className="text-xs text-gray-400">{r.year}</span> : null}
                    {r.views ? <span className="ml-auto text-xs text-gray-400">👁 {r.views}</span> : null}
                  </div>
                  <h2 className="text-lg font-bold text-gray-900 transition group-hover:text-gray-700">{r.title}</h2>
                  {r.issuer ? <p className="mt-1 text-xs text-gray-400">{r.issuer}</p> : null}
                  {r.summary ? <p className="mt-2 line-clamp-3 text-sm text-gray-600">{r.summary}</p> : null}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export const getServerSideProps: GetServerSideProps<Props> = async (ctx) => {
  const game = ctx.params?.game as string
  const q = (ctx.query.q as string) || ''
  const category = (ctx.query.category as string) || ''
  const rules = await fetchRules({ game, q, category })
  const gameLabel = GAME_LABELS[game] || game
  const seo: SeoProps = {
    title: `${gameLabel}竞赛规则大全 · 国家/省级/世界规则与赛事规程全文 | 灵缇规则库`,
    description: `${gameLabel}竞赛规则汇总:收录 ${rules.length} 份${gameLabel}规则与规程,含官方、世界、地方规则及赛事配套制度,可在线查阅全文。${rules
      .slice(0, 4)
      .map((r) => `《${r.title}》`)
      .join('')}`.slice(0, 160),
    keywords: `${gameLabel}竞赛规则,${gameLabel}规则,${gameLabel}比赛规则,${gameLabel}裁判规则,灵缇规则库`,
    path: `/rules/g/${game}`,
    noindex: !!(q || category),
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${gameLabel}竞赛规则`,
        url: absUrl(`/rules/g/${game}`),
        mainEntity: {
          '@type': 'ItemList',
          itemListElement: rules.map((r, i) => ({ '@type': 'ListItem', position: i + 1, url: absUrl(`/rules/${r.slug}`), name: r.title })),
        },
      },
      breadcrumbLd([
        ['首页', '/'],
        ['规则库', '/rules'],
        [`${gameLabel}规则`, `/rules/g/${game}`],
      ]),
    ],
  }
  return { props: { game, gameLabel, rules, q, category, seo } }
}
